import {Logger, CommonTypes, createEventEmmiter, IEventEmitter, EventEmitterCached} from 'neo-common';
import {
    GameActions, ClientHostActionGroup, ClientHostPaytableRequest, ClientHostVolumeData, ClientHostMetadataTypes, ClientHostEndGameRequest,
    ClientHostDialogRequest, ClientHostDispatchExitRequest, IClientHostSupportFlags, IGameEventData, OnOffState
} from './Types';
import {IClientHost} from './IClientHost';
import {IClientHostConsumer} from './IClientHostConsumer';
import {AppContextBase} from '../GameEngine/AppContextBase';
import {GameConfigurationBase} from '../GameEngine/GameConfigurationBase';
import {GameDefinitionBase} from '../GameEngine/GameDefinitionBase';
import {IApp} from '../GameEngine/IApp';
import {IGameConfigurationProxy} from '../GameEngine/Types';

function eventEmitterLogMessage(isError: boolean, group: string, message: string, error?: Error) {
    if (DEBUG) {
        if (isError) {
            Logger.errorGroup(group, error, message);
        } else {
            Logger.debugGroup(group, message);
        }
    }
}

/**
 * @class ClientHost - base abstract class for client host implementation
 */
export abstract class ClientHostBase implements IClientHost {
    protected _eventEmitter: IEventEmitter;
    protected _clientHostConsumer: IClientHostConsumer;
    protected _appContext: AppContextBase<GameConfigurationBase, GameDefinitionBase>;

    constructor(clientHostConsumer: IClientHostConsumer) {
        this._clientHostConsumer = clientHostConsumer;
        this._eventEmitter = new EventEmitterCached(createEventEmmiter(), 5, this.type, eventEmitterLogMessage);

        this.dispatchAction = this.dispatchAction.bind(this);
        this.dispatchExit = this.dispatchExit.bind(this);
    }

    public initialize(appContext: AppContextBase<GameConfigurationBase, GameDefinitionBase>): Promise<any> {
        this._appContext = appContext;
        return this.doInitialize();
    }

    /**
     * @property version - version of client host API implementation
     */
    abstract get version(): string;

    /**
     * @property type - type name of client host API implementation
     */
    abstract get type(): string;

    /**
     * Support flags.
     */
    abstract get supports(): IClientHostSupportFlags;

    protected abstract doInitialize(): Promise<any>;

    public on(event: string, callback: (...args: any[]) => void): void {
        this._eventEmitter.on(event, callback);
    }

    public removeEventListener(event: string): void {
        try {
            this._eventEmitter.removeAllListeners(event);
        } catch (err) {
            Logger.errorGroup(this.type, `remote all listeners for event '${event}' throws error.`, err);
        }
    }

    protected emitEvent(event: string, ...args: any[]): void {
        try {
            this._eventEmitter.emit(event, ...args);
        } catch (err) {
            Logger.errorGroup(this.type, `emit event '${event}' throws error.`, err);
        }
    }

    /**
     * @method dispatchAction - dispatches action from client host to the game
     * @param actionGroup - group name where action belongs to
     * @param action - name of action to handle
     */
    protected dispatchAction(actionGroup: ClientHostActionGroup | string, action: string): void {
        this._clientHostConsumer.handleAction(actionGroup.toString(), action);
    }

    /**
     * @method dispatchExit - dispatched 'exit' event from client host to the game
     * @param request - exit request info.
     * @param callback - function needs to be called when game finished its exit to notify client host about completed exit routine.
     */
    protected dispatchExit(request: ClientHostDispatchExitRequest, callback: CommonTypes.ErrorFunction): void {
        this._clientHostConsumer.handleExit(request, callback);
    }

    /**
     * @method dispatchRefreshAccount - dispatching request from client host to the game to refresh account info.
     */
    protected dispatchRefreshAccount(): void {
        this._clientHostConsumer.handleRefreshAccount();
    }

    /**
     * @method dispatchError - dispatching error from client host to game
     * @param error - error message
     * @param data - structure with information about error
     */
    protected dispatchError(error: string, data?: any): void {
        this._clientHostConsumer.handleError(error, data);
    }

    /**
     * @method dispatchPaytableRequest - dispatch paytable request (load, show, ...) from client host to game
     * @param request - request to be dispatched
     */
    protected dispatchPaytableRequest(request: ClientHostPaytableRequest): void {
        this._clientHostConsumer.handlePaytableRequest(request);
    }

    protected emitEventExternalMessage(message: Object, externalSourceId: string): void {
        this.emitEvent('external-message', message, externalSourceId);
    }

    /**
     * @method actionsUpdated - notification about updated actions.
     * notify client host implementation that game updates its actions list.
     * @param actions - list of updated actions.
     * @summary implementors must handle this method to update itself with list of updateds actions.
     */
    public abstract actionsUpdated(actions: GameActions): void;

    /**
     * @method endGame - request to end game with reason specified.
     * @param request - end game request
     */
    public abstract endGame(app: IApp, request: ClientHostEndGameRequest): void;

    /**
     * called from app when history of played games is requested.
     */
    public abstract history(app: IApp): void;

    /**
     * Continues execution of paused game (after some dialog, etc).
     */
    public abstract continue(app: IApp): void;

    /**
     * requests to show dialog with message (optionally error)
     * @param request - show dialog request data
     */
    public abstract showDialog(request: ClientHostDialogRequest): void;

    /**
     * request to close dialog (if is still visible)
     */
    public abstract closeDialog(): void;

    /**
     * @method sendRequestToPaytable - send action (with optional value) from client host to paytable.
     * @param request - request to be dispatched to paytable
     */
    public abstract sendRequestToPaytable(request: ClientHostPaytableRequest): void;

    /**
     * @method getVolume - gets data about current volume settings within client host.
     */
    public abstract getVolume(): ClientHostVolumeData;

    /**
     * @method setVolume - sets current volume within client host.
     * @param stepNo - volume setting represented by step number from range 0-3.
     */
    public abstract setVolume(stepNo: number): void;

    /**
     * gets metadata value for specified @type.
     * @param type of metadata to get value of.
     */
    public abstract getMetadata<T>(type: ClientHostMetadataTypes): T;

    /**
     * Notification called from game to client host telling that game was loaded with parameter @loadingSucceed indicating success or failure of load.
     */
    public abstract gameLoaded(app: IApp): void;

    /**
     * Game implmentation used this method to notify client host about its custom event which occurs in game.
     */
    public abstract notifyEventFromGame(eventName: string, eventData?: any): void;
    public abstract notifyEventFromGame(eventData: IGameEventData): void;
    public abstract notifyEventFromGame(eventName: string | IGameEventData, eventData?: any): void;

    /**
     * Gets array of allowed languages.
     * @returns {Array<string>}
     */
    public abstract getAllowedLanguages(): Array<string>;

    /**
     * Gets current denomination value.
     * @returns {number}
     */
    public abstract getDenomination(): number;

    /**
     * Set current denomination.
     * @param {number} denomination value. Must be within range of allowed denominations.
     */
    public abstract setDenomination(denomination: number): void;

    /**
     * Gets current language within client host.
     * @returns {string}
     */
    public abstract getLanguage(): string;

    /**
     * Sets current language.
     * @param {string} language code
     */
    public abstract setLanguage(language: string): void;

    /**
     * Gets state of background music (on/off).
     * @returns {OnOffState}
     */
    abstract getBackgroundMusic(): OnOffState;

    /**
     * Updates background music on(true) or off(false).
     */
    abstract setBackgroundMusic(value: OnOffState): void;

    /**
     * Gets shared setting by specified key.
     *
     * @param {string} key key of setting
     * @returns {string}
     * @memberof ILobbyClientHost
     */
    abstract getSharedSetting(key: string): string;

    /**
     * Gets game setting by specified key.
     *
     * @param {string} key key of setting
     * @returns {string}
     * @memberof ILobbyClientHost
     */
    abstract getGameSetting(key: string): string;

    /**
     * Update shared settings value under section 'shared' in key-value pair on client host.
     *
     * @param {string} key Key of setting.
     * @param {string} value  value of setting.
     * @memberof IStyxLobbyTerminalApi
     */
    abstract updateSharedSetting(key: string, value: string): void;

    /**
     * Update game settings value under section 'game' in key-value pair on client host.
     *
     * @param {string} key Key of setting.
     * @param {string} value  value of setting.
     * @memberof IStyxLobbyTerminalApi
     */
    abstract updateGameSetting(key: string, value: string): void;

    /**
     * Updates game configuration before loading game process.
     * @param {IGameDefinitionProxy} gameConfiguration
     */
    abstract updateGameConfiguration(gameConfiguration: IGameConfigurationProxy): Promise<void>;

    /**
     * Sets LED stripe color and mode.
     *
     * @param {string} rgb - color value in format: r;g;b (example: 255;255;255)
     * @param {number} mode - mode of update, allowed values: 0 - default(set), 1 - blink, 2 - fadeout
     */
    abstract setLEDStripe(rgb: string, mode: number): void;

    protected abstract getLogger(): string;

    /**
     * Sends message to external destination. Client host implementators will define its own externals.
     * @param message - message (JSON object) to be send into external destination
     * @param targetExternalIds - list of identification of external targets where to send message.
     */
    public sendMessageToExternal(message: Object, targetExternalIds: string[]): void {
        if (DEBUG) {
            Logger.debugGroup(this.getLogger(), `sendMessageToExternal(targetExternalId: ${JSON.stringify(targetExternalIds)}) ` +
                                                `message: ${JSON.stringify(message)}`);
        }
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/ClientHost/ClientHostBase.ts
 **/